import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore.js'

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0'
  return new Intl.NumberFormat('id-ID').format(value)
}

function MemberDashboard() {
  const { user } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/member/dashboard')
      setData(response.data?.data)
    } catch (error) {
      console.error('Error fetching member dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary"></div>
        <p className="mt-2">Memuat data anggota...</p>
      </div>
    )
  }

  if (!data) {
    return <div className="alert alert-danger">Gagal memuat data. Hubungi administrator.</div>
  }

  const { profil, simpanan, pinjaman, transaksiTerbaru } = data

  return (
    <div className="container-fluid p-3">
      {/* Profile Card */}
      <div className="card bg-primary text-white shadow mb-4">
        <div className="card-body d-flex align-items-center">
          <div className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center mr-3" style={{ width: 60, height: 60, fontSize: 24 }}>
            <i className="fas fa-user"></i>
          </div>
          <div>
            <h4 className="m-0 font-weight-bold">{profil.nama}</h4>
            <small>NBA: {profil.nba} | Bergabung: {profil.tglMasuk} | Status: {profil.statusAnggota === 'AnggotaPenuh' ? 'Anggota Penuh' : profil.statusAnggota}</small>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card border-left-primary shadow h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">Simpanan</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{formatCurrency(simpanan.total)}</div>
                </div>
                <i className="fas fa-piggy-bank fa-2x text-gray-300"></i>
              </div>
              <hr />
              <div className="small">
                <div className="d-flex justify-content-between"><span>Pokok</span><span className="font-weight-bold">{formatCurrency(simpanan.pokok)}</span></div>
                <div className="d-flex justify-content-between"><span>Wajib</span><span className="font-weight-bold">{formatCurrency(simpanan.wajib)}</span></div>
                <div className="d-flex justify-content-between"><span>Sukarela</span><span className="font-weight-bold">{formatCurrency(simpanan.sukarela)}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4 mb-3">
          <div className="card border-left-success shadow h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">Pinjaman Aktif</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{pinjaman.totalAktif} Pinjaman</div>
                </div>
                <i className="fas fa-hand-holding-usd fa-2x text-gray-300"></i>
              </div>
              <hr />
              <div className="small">
                <div className="d-flex justify-content-between"><span>Total Plafond</span><span className="font-weight-bold">{formatCurrency(pinjaman.totalPlafond)}</span></div>
                <div className="d-flex justify-content-between"><span>Sisa Pokok</span><span className="font-weight-bold text-danger">{formatCurrency(pinjaman.totalSisa)}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4 mb-3">
          <div className="card border-left-info shadow h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">Total Simpanan + Pinjaman</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{formatCurrency(simpanan.total + pinjaman.totalPlafond)}</div>
                </div>
                <i className="fas fa-calculator fa-2x text-gray-300"></i>
              </div>
              <hr />
              <Link to={`/members/card?nba=${profil.nba}`} className="btn btn-sm btn-info btn-block">
                <i className="fas fa-id-card mr-1"></i> Kartu Simpan Pinjam
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Active Loans Table */}
      {pinjaman.aktif && pinjaman.aktif.length > 0 && (
        <div className="card shadow mb-4">
          <div className="card-header bg-success text-white">
            <h6 className="m-0 font-weight-bold">Pinjaman Aktif</h6>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>No. Pinjaman</th>
                    <th>Tanggal Cair</th>
                    <th className="text-right">Plafond</th>
                    <th className="text-right">Sisa Pokok</th>
                    <th className="text-right">Bunga</th>
                    <th className="text-right">Sisa Tenor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pinjaman.aktif.map((p, idx) => (
                    <tr key={idx}>
                      <td>{p.NoPinjaman}</td>
                      <td>{p.TglPencairan || '-'}</td>
                      <td className="text-right">{formatCurrency(p.Plafond)}</td>
                      <td className="text-right font-weight-bold text-danger">{formatCurrency(p.SisaPokok)}</td>
                      <td className="text-right">{p.Bunga}%</td>
                      <td className="text-right">{p.SisaTenor}/{p.Tenor}</td>
                      <td><span className="badge badge-success">{p.Status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {transaksiTerbaru && transaksiTerbaru.length > 0 && (
        <div className="card shadow mb-4">
          <div className="card-header bg-info text-white">
            <h6 className="m-0 font-weight-bold">Transaksi Terbaru</h6>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Tanggal</th>
                    <th>Keterangan</th>
                    <th>Akun</th>
                    <th className="text-right">Debet</th>
                    <th className="text-right">Kredit</th>
                  </tr>
                </thead>
                <tbody>
                  {transaksiTerbaru.map((t, idx) => (
                    <tr key={idx}>
                      <td>{t.TglJurnal}</td>
                      <td>{t.Keterangan}</td>
                      <td>{t.NamaAkun}</td>
                      <td className="text-right">{t.Debet > 0 ? formatCurrency(t.Debet) : '-'}</td>
                      <td className="text-right">{t.Kredit > 0 ? formatCurrency(t.Kredit) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemberDashboard